import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import ModalBottom from "../../../../UI/ModalBottom";
import AddEventFormCustomTimeListColck from "../CustomTime/List/Clock";
import {
  DAYS_BEFORE_OPTIONS,
  MAX_ALARMS,
  SAME_DAY_PRESETS,
  SMS_COST_TOMAN,
  alarmKey,
  alarmWallMoment,
  eventWallMoment,
  formatClockFa,
  formatDayFa,
  formatToman,
  getAlarmStatus,
  isDuplicateAlarm,
  resolveAlarms,
  toFaDigits,
} from "../../../../../utils/alarms";

/* ---------- tiny inline icons ---------- */

const BellIcon = ({ className = "w-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.7 21a2 2 0 0 1-3.4 0" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-3.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

/* ---------- small building blocks ---------- */

const Chip = ({ active, invalid, disabled, onClick, title, sub }) => {
  let style = "bg-white border-gray-200 text-gray-700";

  if (active && invalid) style = "bg-rose-50 border-rose-300 text-rose-700";
  else if (active) style = "bg-primary border-primary text-white";

  return (
    <button
      type="button"
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={`flex flex-col items-center gap-0.5 rounded-xl border px-3 py-2 text-sm duration-300 ${style} ${
        disabled ? "opacity-40 cursor-not-allowed" : ""
      }`}
    >
      <span className="font-medium">{title}</span>
      <span className={`text-xs ${active && !invalid ? "text-white/80" : "text-gray-500"}`}>
        {sub}
      </span>
    </button>
  );
};

const DayRow = ({ active, invalid, disabled, onClick, title, sub }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={active}
    disabled={disabled}
    onClick={onClick}
    className={`flex items-center gap-3 w-full rounded-xl border p-3 text-right duration-300 ${
      active && invalid
        ? "border-rose-300 bg-rose-50"
        : active
          ? "border-primary bg-white shadow-custom-md"
          : "border-gray-200 bg-white"
    } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
  >
    <span
      className={`w-5 h-5 shrink-0 rounded-md border flex items-center justify-center duration-300 ${
        active ? (invalid ? "bg-rose-500 border-rose-500" : "bg-primary border-primary") : "border-gray-300 bg-white"
      }`}
    >
      {active && <CheckIcon />}
    </span>

    <span className="flex flex-col">
      <span className="text-gray-800">{title}</span>
      <span className={`text-xs ${active && invalid ? "text-rose-600" : "text-gray-500"}`}>{sub}</span>
    </span>
  </button>
);

/* ---------- custom clock (same day as the event) ---------- */

const pad = (value) => String(value).trim().padStart(2, "0");

const CustomClockModal = ({ eventWall, nowMs, alarms, onAdd, onClose }) => {
  const [showing, setShowing] = useState(false);

  // start 30 minutes before the event, but never before 00:00 of the event day
  const [time, setTime] = useState(() => {
    const start = eventWall.clone().startOf("day");
    const guess = eventWall.clone().subtract(30, "minutes");

    return (guess.isBefore(start) ? start : guess).format("HH:mm");
  });

  useEffect(() => {
    document.body.classList.add("modal-opened");

    return () => document.body.classList.remove("modal-opened");
  }, []);

  const candidate = { kind: "clock", value: time };
  const status = getAlarmStatus(candidate, eventWall, nowMs);
  const duplicate = isDuplicateAlarm(alarms, candidate, eventWall);

  let error = "";
  if (status === "after") error = "ساعت هشدار باید قبل از ساعت رویداد باشد";
  else if (status === "past") error = "این ساعت گذشته است";
  else if (duplicate) error = "این هشدار قبلاً اضافه شده است";

  const close = () => {
    setShowing(false);
    document.body.classList.remove("modal-opened");

    setTimeout(onClose, 300);
  };

  const setHour = ({ hour }) => setTime((prev) => `${pad(hour)}:${prev.split(":")[1]}`);
  const setMin = ({ min }) => setTime((prev) => `${prev.split(":")[0]}:${pad(min)}`);

  const submit = () => {
    if (error) return;

    onAdd(candidate);
    close();
  };

  return (
    <ModalBottom
      show
      onSetShow={(value) => !value && onClose()}
      showing={showing}
      onSetShowing={setShowing}
      className="overflow-y-auto no-scrollbar"
    >
      <div className="sticky top-0 left-0 bg-white w-full z-[2] border-b border-b-slate-200 mb-4">
        <div className="py-2 mb-2">
          <div className="mx-auto w-20 h-2 bg-gray-200 rounded-2xl"></div>
        </div>

        <div className="flex items-center justify-between gap-4 mb-4">
          <h4 className="font-medium flex items-center gap-1.5">
            <BellIcon className="w-[18px] text-gray-500" />
            ساعت دلخواه برای هشدار
          </h4>

          <span className="text-primary font-bold text-lg" dir="ltr">
            {toFaDigits(time)}
          </span>
        </div>
      </div>

      <AddEventFormCustomTimeListColck
        time={time}
        onSetHourOfTime={setHour}
        onSetMinOfTime={setMin}
      />

      <p className="text-sm text-gray-500 text-center mt-4">
        هشدار در همان روز رویداد ({formatDayFa(eventWall)}) ارسال می‌شود.
      </p>

      <div className="min-h-[1.5rem] mt-2 text-center text-sm text-orange-700">{error}</div>

      <button
        type="button"
        onClick={submit}
        disabled={!!error}
        className={`w-full mt-2 rounded-xl py-3 text-white bg-primary duration-300 ${
          error ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        افزودن هشدار
      </button>
    </ModalBottom>
  );
};

/* ---------- main field ---------- */

const AddEventFormAlarms = ({ englishDate, clock, alarms = [], sendSms, onChange }) => {
  const differentDate = useSelector((state) => state.events.computerDifferentTime);

  const [customOpen, setCustomOpen] = useState(false);
  const [, setTick] = useState(0);

  // re-evaluate "already in the past" while the form stays open
  useEffect(() => {
    const timer = setInterval(() => setTick((value) => value + 1), 30000);

    return () => clearInterval(timer);
  }, []);

  const eventWall = useMemo(() => eventWallMoment(englishDate, clock), [englishDate, clock]);

  const nowMs = Date.now() + (differentDate || 0);
  const selected = new Set(alarms.map(alarmKey));

  const resolved = eventWall ? resolveAlarms(alarms, eventWall, nowMs) : [];
  const customAlarms = alarms.filter((item) => item.kind === "clock");

  const limitReached = () => {
    if (alarms.length < MAX_ALARMS) return false;

    toast.warning(`حداکثر ${toFaDigits(MAX_ALARMS)} هشدار می‌توانید تنظیم کنید`);

    return true;
  };

  const toggle = (option) => {
    const key = alarmKey(option);

    if (selected.has(key)) {
      onChange(alarms.filter((item) => alarmKey(item) !== key));
      return;
    }

    if (limitReached()) return;

    onChange([...alarms, { kind: option.kind, value: option.value }]);
  };

  const addCustom = (alarm) => {
    if (limitReached()) return;

    onChange([...alarms, alarm]);
  };

  if (!eventWall) {
    return (
      <div className="flex flex-col gap-2.5">
        <span className="text-gray-500 text-sm"> تنظیم هشدار : </span>
        <div className="h-24 rounded-xl bg-gray-100 animate-pulse"></div>
      </div>
    );
  }

  const statusOf = (alarm) => getAlarmStatus(alarm, eventWall, nowMs);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <span className="text-gray-500 text-sm flex items-center gap-1.5">
          <BellIcon className="w-4" />
          تنظیم هشدار :
        </span>

        {alarms.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-xs text-rose-600"
          >
            حذف همه
          </button>
        )}
      </div>

      {/* same day */}
      <div className="flex flex-col gap-2.5">
        <span className="text-sm text-gray-700 font-medium">همان روز رویداد</span>

        <div className="flex flex-wrap gap-2">
          {SAME_DAY_PRESETS.map((option) => {
            const wall = alarmWallMoment(option, eventWall);
            const active = selected.has(alarmKey(option));
            const status = statusOf(option);
            const sameDay = wall.isSame(eventWall, "day");

            return (
              <Chip
                key={alarmKey(option)}
                title={option.text}
                sub={sameDay ? formatClockFa(wall) : `${formatClockFa(wall)} · روز قبل`}
                active={active}
                invalid={active && status !== "ok"}
                disabled={!active && status !== "ok"}
                onClick={() => toggle(option)}
              />
            );
          })}

          {customAlarms.map((alarm) => (
            <Chip
              key={alarmKey(alarm)}
              title={formatClockFa(alarmWallMoment(alarm, eventWall))}
              sub="ساعت دلخواه"
              active
              invalid={statusOf(alarm) !== "ok"}
              onClick={() => toggle(alarm)}
            />
          ))}

          <button
            type="button"
            onClick={() => setCustomOpen(true)}
            className="flex items-center gap-1.5 rounded-xl border border-dashed border-gray-300 px-3 py-2 text-sm text-primary"
          >
            <PlusIcon />
            ساعت دلخواه
          </button>
        </div>
      </div>

      {/* days before – same clock as the event, no time editing */}
      <div className="flex flex-col gap-2.5">
        <span className="text-sm text-gray-700 font-medium">روزهای قبل (هم‌ساعت با رویداد)</span>

        <div className="flex flex-col gap-2">
          {DAYS_BEFORE_OPTIONS.map((option) => {
            const wall = alarmWallMoment(option, eventWall);
            const active = selected.has(alarmKey(option));
            const status = statusOf(option);

            return (
              <DayRow
                key={alarmKey(option)}
                title={option.text}
                sub={`${formatDayFa(wall)} · ساعت ${formatClockFa(wall)}`}
                active={active}
                invalid={active && status !== "ok"}
                disabled={!active && status !== "ok"}
                onClick={() => toggle(option)}
              />
            );
          })}
        </div>
      </div>

      {/* summary */}
      {alarms.length === 0 ? (
        <p className="text-xs text-gray-500">
          هشداری انتخاب نشده است. برای دریافت یادآوری، یک یا چند گزینه را انتخاب کنید.
        </p>
      ) : (
        <div className="flex flex-col gap-1.5 rounded-xl bg-gray-50 px-3 py-2.5 text-sm">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-gray-700">{toFaDigits(resolved.length)} هشدار فعال</span>

            {sendSms ? (
              <span className="text-gray-700">
                هزینه پیامک: {formatToman(resolved.length * SMS_COST_TOMAN)}
              </span>
            ) : (
              <span className="text-gray-500">پیامک غیرفعال است</span>
            )}
          </div>

          {resolved.length < alarms.length && (
            <span className="text-xs text-orange-700">
              {toFaDigits(alarms.length - resolved.length)} هشدار زمانش گذشته است و ثبت نمی‌شود.
            </span>
          )}

          {resolved.length > 1 && (
            <span className="text-xs text-gray-500">
              هر هشدار به‌صورت یک یادآور جداگانه در تقویم ثبت می‌شود.
            </span>
          )}
        </div>
      )}

      {customOpen && (
        <CustomClockModal
          eventWall={eventWall}
          nowMs={nowMs}
          alarms={alarms}
          onAdd={addCustom}
          onClose={() => setCustomOpen(false)}
        />
      )}
    </div>
  );
};

export default AddEventFormAlarms;
